document.addEventListener("DOMContentLoaded", () => {
  class EmblaCarouselManager {
    constructor(carouselElement) {
      // Get the root element
      this.carouselRoot = carouselElement;

      // CHANGE: Scoped query helpers so nested carousels don't grab each other's elements
      this._scopedQuery = (selector) =>
        Array.from(this.carouselRoot.querySelectorAll(selector))
          .find((el) => el.closest('.embla[data-embla-carousel]') === this.carouselRoot) || null;
      this._scopedQueryAll = (selector) =>
        Array.from(this.carouselRoot.querySelectorAll(selector))
          .filter((el) => el.closest('.embla[data-embla-carousel]') === this.carouselRoot);

      // CHANGE: Use scoped queries for viewport/dots/prev/next
      this.viewportNode = this._scopedQuery(".embla__viewport");
      this.dotsNode = this._scopedQuery(".embla__dots");
      this.prevButton = this._scopedQuery(".embla__prev");
      this.nextButton = this._scopedQuery(".embla__next");

      this.loop = this.carouselRoot.getAttribute("data-loop") === "true";
      this.axis = this.carouselRoot.getAttribute("data-axis") || "x";
      this.align = this.carouselRoot.getAttribute("data-align") || "start";
      this.slidesToScroll = this.carouselRoot.getAttribute("data-slides-to-scroll") || "auto";
      this.dragFree = this.carouselRoot.getAttribute("data-drag-free") === "true";
      this.autoPlayEnabled = this.carouselRoot.getAttribute("data-auto-play") === "true";
      this.autoPlayDuration = parseInt(this.carouselRoot.getAttribute("data-auto-play-delay"), 10) || 2000;

      this.emblaOptions = {
        loop: this.loop,
        align: this.align,
        axis: this.axis,
        slidesToScroll: this.slidesToScroll === "auto" ? "auto" : parseInt(this.slidesToScroll, 10),
        dragFree: this.dragFree
      };

      this.embla = null;
      this.init();
    }

    init() {
      if (!window.EmblaCarousel) {
        console.warn(
          "Embla Carousel library not found. Please include the script."
        );
        return;
      }

      // Initialize Embla with plugin enabled
      const plugins = [];

      // Add Autoplay if enabled
      if (this.autoPlayEnabled && window.EmblaCarouselAutoplay) {
        this.autoplayPlugin = EmblaCarouselAutoplay({
          delay: this.autoPlayDuration,
          stopOnMouseEnter: true,
          stopOnInteraction: false
        });
        plugins.push(this.autoplayPlugin);
      }

      this.embla = window.EmblaCarousel(this.viewportNode, this.emblaOptions, plugins);

      // Add event listeners
      this.embla.on("init", () => {
        this.updateActiveSlide();
        this.updateButtonStates();
      });
      this.embla.on("select", () => {
        this.updateActiveSlide();
        this.updateButtonStates();
      });
      this.embla.on("reInit", () => {
        this.updateButtonStates();
      });

      if (this.dotsNode) {
        this.setupDots();
        this.embla.on("select", () => this.selectDot());
        this.embla.on("reInit", () => this.setupDots());
      }

      // Setup navigation buttons
      this.setupNavigationButtons();
    }

    setupNavigationButtons() {
      if (this.prevButton) {
        this.prevButton.addEventListener("click", (e) => {
          e.stopPropagation(); // CHANGE: prevent nested carousels from intercepting
          this.embla.scrollPrev();
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener("click", (e) => {
          e.stopPropagation(); // CHANGE: prevent nested carousels from intercepting
          this.embla.scrollNext();
        });
      }
    }

    updateButtonStates() {
      if (!this.embla) return;

      // Check if carousel can scroll in either direction
      const canScrollPrev = this.embla.canScrollPrev();
      const canScrollNext = this.embla.canScrollNext();

      // Update prev button state
      if (this.prevButton) {
        if (canScrollPrev) {
          this.prevButton.classList.remove("disabled");
          this.prevButton.removeAttribute("disabled");
        } else {
          this.prevButton.classList.add("disabled");
          this.prevButton.setAttribute("disabled", "disabled");
        }
      }

      // Update next button state
      if (this.nextButton) {
        if (canScrollNext) {
          this.nextButton.classList.remove("disabled");
          this.nextButton.removeAttribute("disabled");
        } else {
          this.nextButton.classList.add("disabled");
          this.nextButton.setAttribute("disabled", "disabled");
        }
      }
    }

    createDots() {
      if (!this.embla || !this.dotsNode) return;

      // Check if dots already have content
      const existingDots = this.dotsNode.querySelectorAll('.embla__dot');
      if (existingDots.length > 0) {
        existingDots.forEach((dot, index) => {
          dot.addEventListener('click', () => this.embla.scrollTo(index));
        });
        return;
      }

      this.dotsNode.innerHTML = "";
      const numberOfDots = this.embla.scrollSnapList().length;
      if (numberOfDots <= 1) return;

      Array.from({ length: numberOfDots }).forEach((_, index) => {
        const dot = document.createElement("button");
        dot.classList.add("embla__dot");
        dot.addEventListener("click", () => this.embla.scrollTo(index));
        this.dotsNode.appendChild(dot);
      });
    }

    selectDot() {
      if (!this.embla || !this.dotsNode) return;

      const allDots = Array.from(this.dotsNode.children);
      allDots.forEach((dot, index) => {
        dot.classList.toggle(
          "active",
          this.embla.selectedScrollSnap() === index
        );
      });
      
      // reset autoplay timer on interaction
      if (this.autoPlayEnabled && this.autoplayPlugin) {
        this.autoplayPlugin.reset();
      }
    }

    setupDots() {
      this.createDots();
      this.selectDot();
    }

    updateActiveSlide() {
      if (!this.embla) return;

      const selectedIndex = this.embla.selectedScrollSnap();
      // CHANGE: Scope slides to this carousel only (avoid touching nested slides)
      const slides = this._scopedQueryAll(".embla__slide");

      slides.forEach((slide, index) => {
        if (index === selectedIndex) {
          slide.classList.add("active");
        } else {
          slide.classList.remove("active");
        }
      });
    }

    destroy() {
      if (this.embla) {
        this.embla.destroy();
        this.embla = null;
      }
    }
  }

  // --- Helper to init all carousels in a container ---
  function initCarouselsIn(root = document) {
    const carousels = root.querySelectorAll(".embla[data-embla-carousel]");
    carousels.forEach((carousel) => {
      if (!carousel._emblaInstance) {
        carousel._emblaInstance = new EmblaCarouselManager(carousel);
      }
    });
  }

  // --- Initial load ---
  initCarouselsIn(document);

  // --- MutationObserver for dynamically injected carousels ---
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return; // element nodes only
        if (node.matches?.(".embla[data-embla-carousel]")) {
          initCarouselsIn(node.parentNode);
        } else {
          initCarouselsIn(node);
        }
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // --- Shopify Theme Editor compatibility ---
  document.addEventListener("shopify:section:load", (e) => {
    initCarouselsIn(e.target);
  });
  
});






// web components to utilize embla carousel
// ========================================================================



class EmblaCarousel extends HTMLElement {
  constructor() {
    super();
    this.setAttribute('data-embla-carousel', '');
    this.classList.add('embla');
  }
}
class EmblaSlide extends HTMLElement {
  constructor() {
    super();
    this.classList.add('embla__slide');
  }
}
class EmblaViewport extends HTMLElement {
  constructor() {
    super();
    this.classList.add('embla__viewport');
  }
}
class EmblaContainer extends HTMLElement {
  constructor() {
    super();
    this.classList.add('embla__container');
  }
}
class EmblaDots extends HTMLElement {
  constructor() {
    super();
    this.classList.add('embla__dots');
  }
}
class EmblaPrevButton extends HTMLElement {
  constructor() {
    super();
    this.innerHTML = `
      <button class="embla__prev" aria-label="previous slide">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="1" stroke-linecap="round" stroke-linejoin="round"
            class="lucide lucide-chevron-left-icon lucide-chevron-left">
            <path d="m15 18-6-6 6-6" />
        </svg>
     </button>
    `;
  }
}
class EmblaNextButton extends HTMLElement {
  constructor() {
    super();
    this.innerHTML = `
      <button class="embla__next" aria-label="next slide">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="1" stroke-linecap="round" stroke-linejoin="round"
            class="lucide lucide-chevron-right-icon lucide-chevron-right">
            <path d="m9 18 6-6-6-6" />
        </svg>
      </button>
    `;
  }
}


// Register custom elements
customElements.define('embla-carousel', EmblaCarousel);
customElements.define('embla-viewport', EmblaViewport);
customElements.define('embla-container', EmblaContainer);
customElements.define('embla-slide', EmblaSlide);
customElements.define('embla-navigation-dots', EmblaDots);
customElements.define('embla-prev', EmblaPrevButton);
customElements.define('embla-next', EmblaNextButton);